import * as Api from '@ViewModels';
import { Position, type Edge, type Node } from '@xyflow/react';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { type BaseEdgeData, type BranchEdgeData } from '../types/edges';
import { BRANCH_HORIZONTAL_SPACING, FLOW_VERTICAL_SPACING } from './layoutConstants';
import { BRANCH_COLORS } from '../edges/branchEdgeStyles';
import { applyLayout } from './layoutEngine';

interface ConvertTemplateToFlowResult {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
}

interface ConvertContext {
	userSession?: any;
	impersonationContext?: Api.IImpersonationContext;
	fetchedTemplates: Map<string, Api.IAutomationTemplate>;
	currentX: number;
	currentY: number;
	branchDepth: number;
	template?: Api.IAutomationTemplate;
	visitedInCurrentPath: Set<string>;
}

const MAX_BRANCH_DEPTH = 5;

/**
 * Converts an automation template to React Flow nodes and edges.
 * Supports recursive expansion of switch steps if userSession is provided.
 *
 * @param template - The automation template to convert
 * @param options - Optional configuration for recursive expansion and layout
 */
export async function convertTemplateToFlow(
	template: Api.IAutomationTemplate,
	options?: {
		userSession?: any;
		impersonationContext?: Api.IImpersonationContext;
		expandBranches?: boolean;
	}
): Promise<ConvertTemplateToFlowResult> {
	if (!template) {
		return { nodes: [], edges: [] };
	}

	const version = template.draft || template.published;

	if (!version) {
		return { nodes: [], edges: [] };
	}

	// If expandBranches is enabled and we have userSession, use recursive expansion
	if (options?.expandBranches && options?.userSession) {
		const context: ConvertContext = {
			userSession: options.userSession,
			impersonationContext: options.impersonationContext,
			fetchedTemplates: new Map(),
			currentX: 400,
			currentY: 100,
			branchDepth: 0,
			template,
			visitedInCurrentPath: new Set<string>(),
		};

		const recursiveResult = await convertTemplateToFlowRecursive(version, context, null);

		// Apply dagre hierarchical layout
		return applyLayout(recursiveResult.nodes, recursiveResult.edges);
	}

	// Otherwise use simple linear conversion (backward compatible)
	const simpleResult = convertTemplateToFlowSimple(template, version);

	// Apply dagre hierarchical layout
	return applyLayout(simpleResult.nodes, simpleResult.edges);
}

/**
 * Simple linear conversion without branch expansion (original behavior)
 */
function convertTemplateToFlowSimple(
	template: Api.IAutomationTemplate,
	version: Api.IAutomationTemplateVersion
): ConvertTemplateToFlowResult {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge[] = [];

	const steps = version.steps || [];
	const trigger = version.trigger;

	let currentY = 100;
	const nodeSpacing = 200;
	const baseX = 400;

	if (trigger) {
		const triggerNode: Node<BaseNodeData> = {
			id: 'trigger',
			type: NODE_TYPES.TRIGGER,
			position: { x: baseX, y: currentY },
			data: {
				label: getTriggerLabel(trigger),
				category: 'trigger',
				automationTrigger: trigger,
				metadata: {
					triggerType: trigger._type,
				},
			},
			sourcePosition: Position.Bottom,
		};

		nodes.push(triggerNode);
		currentY += nodeSpacing;
	}

	steps.forEach((step, index) => {
		const stepNode = convertStepToNodeSync(step, index, baseX, currentY, template);
		nodes.push(stepNode);

		const sourceId = index === 0 ? 'trigger' : `step-${steps[index - 1].id}`;
		const edge: Edge = {
			id: `${sourceId}-to-step-${step.id}`,
			source: sourceId,
			target: `step-${step.id}`,
			type: 'default',
		};
		edges.push(edge);

		currentY += nodeSpacing;
	});

	return { nodes, edges };
}

/**
 * Creates the trigger node for the automation flow
 */
function createTriggerNode(
	trigger: Api.IAutomationTrigger | undefined,
	context: ConvertContext,
	baseX: number,
	parentNodeId?: string
): { nodes: Node<BaseNodeData>[]; edges: Edge<BranchEdgeData | BaseEdgeData>[]; triggerId?: string; nextY: number } {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge<BranchEdgeData | BaseEdgeData>[] = [];
	let currentY = context.currentY;

	if (!parentNodeId && trigger) {
		const triggerNode: Node<BaseNodeData> = {
			id: 'trigger',
			type: NODE_TYPES.TRIGGER,
			position: { x: baseX, y: currentY },
			data: {
				label: getTriggerLabel(trigger),
				category: 'trigger',
				automationTrigger: trigger,
				metadata: {
					triggerType: trigger._type,
				},
			},
			sourcePosition: Position.Bottom,
		};
		nodes.push(triggerNode);
		currentY += FLOW_VERTICAL_SPACING;
		return { nodes, edges, triggerId: 'trigger', nextY: currentY };
	}

	return { nodes, edges, triggerId: undefined, nextY: currentY };
}

/**
 * Connects branch result to switch node with appropriate edges and metadata
 */
function connectBranchToSwitch(
	branchResult: ConvertTemplateToFlowResult,
	switchNodeId: string,
	caseStatement: Api.IAutomationStepCaseStatement,
	caseId: string,
	caseIndex: number,
	caseName: string,
	totalCases: number,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): void {
	if (branchResult.nodes.length === 0) {
		return;
	}

	const firstBranchNodeId = branchResult.nodes[0].id;
	const branchColor = caseStatement.isDefault ? '#6B7280' : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];

	const branchEdgeData: BranchEdgeData = {
		condition: caseStatement.filter,
		caseId,
		caseIndex,
		totalCases,
		isDefault: caseStatement.isDefault,
		caseName,
		caseColor: branchColor, // Add color to edge data for consistent visualization
	};

	edges.push({
		id: `${switchNodeId}-case-${caseId}-to-${firstBranchNodeId}`,
		source: switchNodeId,
		sourceHandle: `output-case-${caseId}`,
		target: firstBranchNodeId,
		type: 'branch',
		label: caseName,
		data: branchEdgeData,
	});

	const nodeIndex = nodes.findIndex(n => n.id === firstBranchNodeId);
	if (nodeIndex >= 0) {
		nodes[nodeIndex] = {
			...nodes[nodeIndex],
			data: {
				...nodes[nodeIndex].data,
				parentCaseInfo: {
					caseIndex,
					caseName,
					isDefault: caseStatement.isDefault,
					caseColor: branchColor,
				},
			},
		};
	}
}

/**
 * Validates and fetches child template for a switch branch case
 * Returns the template version if valid, or creates error node and returns null
 */
async function validateAndFetchBranchTemplate(
	childTemplateId: string,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[],
	switchNodeId: string,
	caseId: string,
	caseIndex: number,
	caseStatement: Api.IAutomationStepCaseStatement,
	caseName: string,
	branchX: number,
	branchStartY: number
): Promise<{ steps?: Api.IAutomationStep[]; trigger?: Api.IAutomationTrigger } | null> {
	if (context.visitedInCurrentPath.has(childTemplateId)) {
		console.error(`[convertTemplateToFlow] Circular reference detected: ${childTemplateId}. Skipping branch.`);
		return null;
	}

	if (context.branchDepth >= MAX_BRANCH_DEPTH) {
		console.warn(
			`[convertTemplateToFlow] Maximum branch depth ${MAX_BRANCH_DEPTH} reached at template ${childTemplateId}.`
		);
		return null;
	}

	const childTemplate = await fetchTemplateById(childTemplateId, context);

	if (!childTemplate) {
		createErrorNode(
			nodes,
			edges,
			switchNodeId,
			caseId,
			caseIndex,
			caseStatement,
			caseName,
			branchX,
			branchStartY,
			childTemplateId,
			'template_fetch_failed',
			'Failed to Load Template'
		);
		return null;
	}

	const childVersion = childTemplate.published || childTemplate.draft;

	if (!childVersion) {
		createErrorNode(
			nodes,
			edges,
			switchNodeId,
			caseId,
			caseIndex,
			caseStatement,
			caseName,
			branchX,
			branchStartY,
			childTemplateId,
			'no_version_available',
			'No Version Available'
		);
		return null;
	}

	return childVersion;
}

/**
 * Processes a single branch case within a switch step
 */
async function processSwitchBranchCase(
	caseStatement: Api.IAutomationStepCaseStatement,
	caseIndex: number,
	switchNodeId: string,
	branchX: number,
	branchStartY: number,
	totalCases: number,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): Promise<void> {
	const caseId = caseStatement.id || `case-${caseIndex}`;
	const childTemplateId = caseStatement.automationTemplateId;
	const caseName = caseStatement.isDefault ? 'Default' : caseStatement.name || `Case ${caseIndex + 1}`;

	if (!childTemplateId || !context.userSession) {
		return;
	}

	const childVersion = await validateAndFetchBranchTemplate(
		childTemplateId,
		context,
		nodes,
		edges,
		switchNodeId,
		caseId,
		caseIndex,
		caseStatement,
		caseName,
		branchX,
		branchStartY
	);

	if (!childVersion) {
		return;
	}

	const newVisitedPath = new Set(context.visitedInCurrentPath);
	newVisitedPath.add(childTemplateId);

	const branchContext: ConvertContext = {
		...context,
		currentX: branchX,
		currentY: branchStartY,
		branchDepth: context.branchDepth + 1,
		visitedInCurrentPath: newVisitedPath,
	};

	const branchResult = await convertTemplateToFlowRecursive(childVersion, branchContext, switchNodeId);

	nodes.push(...branchResult.nodes);
	edges.push(...branchResult.edges);

	connectBranchToSwitch(
		branchResult,
		switchNodeId,
		caseStatement,
		caseId,
		caseIndex,
		caseName,
		totalCases,
		nodes,
		edges
	);
}

/**
 * Processes a switch/conditional automation step and expands its branches
 */
async function processSwitchStep(
	step: Api.ISwitchAutomationStep,
	stepIndex: number,
	parentNodeId: string,
	baseX: number,
	currentY: number,
	context: ConvertContext,
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[]
): Promise<{ maxY: number }> {
	const switchNodeId = `step-${step.id}`;
	const cases = step.cases || [];

	const switchNode = await convertStepToNodeAsync(step, stepIndex, baseX, currentY, context);
	switchNode.data.outputHandles = cases.map((caseStmt, idx) => `output-case-${caseStmt.id || idx}`);
	switchNode.data.summary = cases.length > 0 ? `${cases.length} branches` : 'No branches';

	nodes.push(switchNode);

	const previousNodeId =
		stepIndex === 0
			? parentNodeId
			: `step-${context.template?.draft?.steps?.[stepIndex - 1]?.id || context.template?.published?.steps?.[stepIndex - 1]?.id}`;
	edges.push({
		id: `${previousNodeId}-to-${switchNodeId}`,
		source: previousNodeId,
		target: switchNodeId,
		type: 'default',
	});

	let maxY = currentY + FLOW_VERTICAL_SPACING;

	if (cases.length > 0) {
		const branchCount = cases.length;

		// Vertical layout: branches go below the switch node, spread horizontally
		// Position branches below the switch with proper spacing
		const switchNodeHeight = 100; // Approximate switch node height
		const branchStartY = currentY + switchNodeHeight + FLOW_VERTICAL_SPACING;
		const totalBranchWidth = (branchCount - 1) * BRANCH_HORIZONTAL_SPACING;
		const startX = baseX - totalBranchWidth / 2;

		for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
			const caseStatement = cases[caseIndex];
			const branchX = startX + caseIndex * BRANCH_HORIZONTAL_SPACING;

			await processSwitchBranchCase(
				caseStatement,
				caseIndex,
				switchNodeId,
				branchX,
				branchStartY,
				branchCount,
				context,
				nodes,
				edges
			);
		}

		// Calculate the maximum extent of branches
		const maxBranchY = Math.max(
			...nodes.filter(n => n.position.y >= branchStartY).map(n => n.position.y),
			branchStartY
		);
		maxY = maxBranchY + FLOW_VERTICAL_SPACING;
	}

	return { maxY };
}

/**
 * Creates an error node for failed template loading
 */
function createErrorNode(
	nodes: Node<BaseNodeData>[],
	edges: Edge<BranchEdgeData | BaseEdgeData>[],
	switchNodeId: string,
	caseId: string,
	caseIndex: number,
	caseStatement: Api.IAutomationStepCaseStatement,
	caseName: string,
	x: number,
	y: number,
	templateId: string,
	errorType: string,
	errorLabel: string
): void {
	const errorNodeId = `error-${errorType}-${templateId}-${caseIndex}`;
	const errorNode: Node<BaseNodeData> = {
		id: errorNodeId,
		type: NODE_TYPES.ERROR,
		position: { x, y },
		data: {
			label: errorLabel,
			summary: `Template ID: ${templateId}`,
			category: 'action',
			metadata: {
				error: errorType,
				templateId,
			},
		},
		sourcePosition: Position.Bottom,
		targetPosition: Position.Top,
	};

	nodes.push(errorNode);

	edges.push({
		id: `${switchNodeId}-case-${caseId}-to-${errorNodeId}`,
		source: switchNodeId,
		sourceHandle: `output-case-${caseId}`,
		target: errorNodeId,
		type: 'branch',
		label: caseName,
		data: {
			condition: caseStatement.filter,
			caseId,
			caseIndex,
			isDefault: caseStatement.isDefault,
			caseName,
		},
	});
}

/**
 * Processes a regular (non-switch) automation step
 */
async function processRegularStep(
	step: Api.IAutomationStep,
	stepIndex: number,
	parentNodeId: string,
	previousStepId: string | undefined,
	baseX: number,
	currentY: number,
	context: ConvertContext
): Promise<{ node: Node<BaseNodeData>; edge: Edge; nextY: number }> {
	const stepNode = await convertStepToNodeAsync(step, stepIndex, baseX, currentY, context);

	const sourceId = stepIndex === 0 ? parentNodeId : `step-${previousStepId}`;
	const edge: Edge = {
		id: `${sourceId}-to-step-${step.id}`,
		source: sourceId,
		target: `step-${step.id}`,
		type: 'default',
	};

	return {
		node: stepNode,
		edge,
		nextY: currentY + FLOW_VERTICAL_SPACING,
	};
}

/**
 * Recursively converts template to flow with branch expansion
 */
async function convertTemplateToFlowRecursive(
	version: { steps?: Api.IAutomationStep[]; trigger?: Api.IAutomationTrigger },
	context: ConvertContext,
	parentNodeId: string | null
): Promise<ConvertTemplateToFlowResult> {
	const nodes: Node<BaseNodeData>[] = [];
	const edges: Edge[] = [];

	const steps = version.steps || [];
	const trigger = version.trigger;

	const baseX = parentNodeId ? context.currentX + BRANCH_HORIZONTAL_SPACING : context.currentX;
	let currentY = context.currentY;

	const triggerResult = createTriggerNode(trigger, context, baseX, parentNodeId || undefined);
	nodes.push(...triggerResult.nodes);
	edges.push(...triggerResult.edges);
	currentY = triggerResult.nextY;

	const effectiveParentId = parentNodeId || triggerResult.triggerId || 'trigger';

	for (let i = 0; i < steps.length; i++) {
		const step = steps[i];
		const previousStepId = i > 0 ? steps[i - 1].id : undefined;

		if (step._type === 'SwitchAutomationStep') {
			const switchStep = step as Api.ISwitchAutomationStep;
			const switchResult = await processSwitchStep(
				switchStep,
				i,
				effectiveParentId,
				baseX,
				currentY,
				context,
				nodes,
				edges
			);
			currentY = switchResult.maxY;
		} else {
			const regularResult = await processRegularStep(
				step,
				i,
				effectiveParentId,
				previousStepId,
				baseX,
				currentY,
				context
			);
			nodes.push(regularResult.node);
			edges.push(regularResult.edge);
			currentY = regularResult.nextY;
		}
	}

	return { nodes, edges };
}

/**
 * Fetches a template by ID with caching
 */
async function fetchTemplateById(templateId: string, context: ConvertContext): Promise<Api.IAutomationTemplate | null> {
	// Check cache first
	if (context.fetchedTemplates.has(templateId)) {
		return context.fetchedTemplates.get(templateId)!;
	}

	if (!context.userSession) {
		return null;
	}

	try {
		const template = (await context.userSession.webServiceHelper.callAsync(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}`,
				impersonationContext: context.impersonationContext,
			}),
			'GET'
		)) as Api.IAutomationTemplate;

		context.fetchedTemplates.set(templateId, template);
		return template;
	} catch (error) {
		console.error(`[fetchTemplateById] Failed to fetch template ${templateId}:`, error);
		return null;
	}
}

/**
 * Async version of convertStepToNode for use with recursive expansion
 */
async function convertStepToNodeAsync(
	step: Api.IAutomationStep,
	index: number,
	x: number,
	y: number,
	context: ConvertContext
): Promise<Node<BaseNodeData>> {
	return convertStepToNodeSync(step, index, x, y, context.template);
}

/**
 * Synchronous step to node conversion (original implementation)
 */
function convertStepToNodeSync(
	step: Api.IAutomationStep,
	index: number,
	x: number,
	y: number,
	template?: Api.IAutomationTemplate
): Node<BaseNodeData> {
	const nodeType = getNodeTypeForStep(step._type);
	const stepTypeEnum = Api.apiModelTypeToAutomationStepType(step._type as Api.ApiModelType);

	// Ensure we have a valid stepType enum value - keep error log for critical issues
	if (!stepTypeEnum) {
		console.error('[convertStepToNodeSync] Failed to convert step type:', {
			stepId: step.id,
			stepType: step._type,
			mappedNodeType: nodeType,
			stepTypeEnum,
		});
	}

	// Extract schedule with proper type guard
	const schedule: Api.IAutomationStepSchedule | undefined =
		'schedule' in step && step.schedule !== null && typeof step.schedule === 'object'
			? (step.schedule as Api.IAutomationStepSchedule)
			: undefined;

	const stepData: AutomationStepNodeData = {
		nodeId: `step-${step.id}`,
		stepId: step.id,
		stepType: stepTypeEnum || Api.AutomationStepType.Email, // Fallback to Email if conversion fails
		label: getStepLabel(step, index),
		category: 'action',
		automationStep: step,
		automationTemplate: template,
		isExpanded: false,
		isCompact: true,
		isValid: validateStep(step),
		validationErrors: [],
		stepData: {
			name: ('name' in step ? step.name : '') || '',
			schedule,
			hasContent: checkStepHasContent(step),
		},
		metadata: {
			stepType: step._type,
		},
	};

	const node: Node<BaseNodeData> = {
		id: `step-${step.id}`,
		type: nodeType,
		position: { x, y },
		data: stepData,
		sourcePosition: Position.Bottom,
		targetPosition: Position.Top,
	};

	return node;
}

const STEP_TYPE_TO_NODE_TYPE: Record<string, string> = {
	EmailAutomationStep: NODE_TYPES.EMAIL_STEP,
	TextingAutomationStep: NODE_TYPES.TEXTING_STEP,
	ActionItemAutomationStep: NODE_TYPES.ACTION_ITEM_STEP,
	AddTagAutomationStep: NODE_TYPES.ADD_TAG_STEP,
	RemoveTagAutomationStep: NODE_TYPES.REMOVE_TAG_STEP,
	HandwrittenCardAutomationStep: NODE_TYPES.HANDWRITTEN_CARD_STEP,
	NoActionAutomationStep: NODE_TYPES.NO_ACTION_STEP,
	OpportunityAutomationStep: NODE_TYPES.OPPORTUNITY_STEP,
	HtmlNewsletterAutomationStep: NODE_TYPES.HTML_NEWSLETTER_STEP,
	SwitchAutomationStep: NODE_TYPES.SWITCH_STEP,
};

export function getNodeTypeForStep(stepType: string): string {
	const result = STEP_TYPE_TO_NODE_TYPE[stepType] || NODE_TYPES.EMAIL_STEP;
	return result;
}

export function getStepLabel(step: Api.IAutomationStep, index: number): string {
	const stepName = 'name' in step ? step.name : undefined;
	if (stepName && typeof stepName === 'string') {
		return stepName;
	}

	const stepTypeLabel = getStepTypeLabel(step._type);
	return `${stepTypeLabel} ${index + 1}`;
}

function getStepTypeLabel(stepType: string): string {
	switch (stepType) {
		case 'EmailAutomationStep':
			return 'Email';
		case 'TextingAutomationStep':
			return 'Text Message';
		case 'ActionItemAutomationStep':
			return 'Action Item';
		case 'AddTagAutomationStep':
			return 'Add Tag';
		case 'RemoveTagAutomationStep':
			return 'Remove Tag';
		case 'HandwrittenCardAutomationStep':
			return 'Handwritten Card';
		case 'NoActionAutomationStep':
			return 'Wait';
		case 'OpportunityAutomationStep':
			return 'Opportunity';
		case 'HtmlNewsletterAutomationStep':
			return 'HTML Newsletter';
		case 'SwitchAutomationStep':
			return 'Conditional';
		default:
			return 'Step';
	}
}

/**
 * Converts a trigger's string _type to the AutomationTriggerType enum value.
 * This is needed because AutomationTriggerIcon expects enum values, not strings.
 * Since the enum values are the same strings, we can directly map them.
 */
export function normalizeTriggerType(trigger: Api.IAutomationTrigger): Api.IAutomationTrigger {
	if (!trigger || !trigger._type) {
		return trigger;
	}

	const triggerType = trigger._type as string;
	let normalizedType: Api.AutomationTriggerType | string | undefined;

	// Map string types to enum values (enum values are the same strings)
	switch (triggerType) {
		case 'NewClientAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewClient;
			break;
		case 'NewLeadAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewLead;
			break;
		case 'TagAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Tag;
			break;
		case 'AutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Meeting;
			break;
		case 'EventRegistrationTrigger':
			normalizedType = Api.AutomationTriggerType.EventRegistration;
			break;
		case 'ResourceSelectorAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.ResourceSelector;
			break;
		case 'TextingCampaignAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Texting;
			break;
		case 'ManualAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.Manual;
			break;
		case 'NewDonorAutomationTrigger':
			normalizedType = Api.AutomationTriggerType.NewDonor;
			break;
		case 'NewDonationTrigger':
			normalizedType = Api.AutomationTriggerType.NewDonationTrigger;
			break;
		default:
			// Return as-is if no mapping found
			return trigger;
	}

	// Create a new trigger object with the normalized type
	// AutomationTriggerType enum values are string literals that match the expected _type values
	return {
		...trigger,
		_type: normalizedType as Api.IAutomationTrigger['_type'],
	} as Api.IAutomationTrigger;
}

export function getTriggerLabel(trigger: Api.IAutomationTrigger): string {
	const triggerType = trigger._type as string;

	if (triggerType === 'NewClientAutomationTrigger') {
		return 'New Client';
	}
	if (triggerType === 'NewLeadAutomationTrigger') {
		return 'New Lead';
	}
	if (triggerType === 'TagAutomationTrigger') {
		return `Tag: ${(trigger as Api.ITagAutomationTrigger).tag || 'Applied'}`;
	}
	if (triggerType === 'AutomationTrigger') {
		return 'Meeting Scheduled';
	}
	if (triggerType === 'EventRegistrationTrigger') {
		return 'Event Registration';
	}
	if (triggerType === 'ResourceSelectorAutomationTrigger') {
		return getResourceSelectorLabel(trigger as Api.IResourceSelectorAutomationTrigger);
	}
	if (triggerType === 'TextingCampaignAutomationTrigger') {
		return 'Texting Reply';
	}
	if (triggerType === 'ManualAutomationTrigger') {
		return 'Manual Trigger';
	}

	return 'Trigger';
}

function getResourceSelectorLabel(trigger: Api.IResourceSelectorAutomationTrigger): string {
	const resourceSelector = trigger.resourceSelector;
	if (!resourceSelector) {
		return 'Custom Date';
	}
	switch (resourceSelector) {
		case Api.ResourceSelectorId.HappyBirthday:
			return 'Birthday';
		case Api.ResourceSelectorId.PolicyRenew:
			return 'Policy Renewal';
		case Api.ResourceSelectorId.FinancialReview:
			return 'Financial Review';
		default:
			return String(resourceSelector) || 'Custom Date';
	}
}

export function validateStep(step: Api.IAutomationStep): boolean {
	switch (step._type) {
		case 'EmailAutomationStep': {
			const emailStep = step as Api.IEmailAutomationStep;
			return Boolean(emailStep.name && (emailStep.subject || emailStep.content));
		}
		case 'TextingAutomationStep': {
			const textingStep = step as Api.ITextingAutomationStep;
			return Boolean(textingStep.content);
		}
		case 'ActionItemAutomationStep': {
			const actionItemStep = step as Api.IActionItemAutomationStep;
			return Boolean(actionItemStep.content);
		}
		case 'AddTagAutomationStep':
		case 'RemoveTagAutomationStep': {
			const tagStep = step as Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep;
			return Boolean(tagStep.tagName);
		}
		case 'HandwrittenCardAutomationStep': {
			const hwcStep = step as Api.HandwrittenCardAutomationStep;
			return Boolean(hwcStep.content && hwcStep.templateReference);
		}
		case 'HtmlNewsletterAutomationStep': {
			const newsletterStep = step as Api.IHtmlNewsletterAutomationStep;
			return Boolean(newsletterStep.subject || newsletterStep.content);
		}
		case 'SwitchAutomationStep': {
			const switchStep = step as Api.ISwitchAutomationStep;
			// Switch steps are valid if they have at least one case
			return Boolean(switchStep.cases && switchStep.cases.length > 0);
		}
		case 'OpportunityAutomationStep': {
			return true;
		}
		case 'NoActionAutomationStep': {
			return true;
		}
		default:
			return false;
	}
}

export function checkStepHasContent(step: Api.IAutomationStep): boolean {
	switch (step._type) {
		case 'EmailAutomationStep': {
			const emailStep = step as Api.IEmailAutomationStep;
			return Boolean(emailStep.content || emailStep.subject);
		}
		case 'TextingAutomationStep': {
			const textingStep = step as Api.ITextingAutomationStep;
			return Boolean(textingStep.content);
		}
		case 'ActionItemAutomationStep': {
			const actionItemStep = step as Api.IActionItemAutomationStep;
			return Boolean(actionItemStep.content);
		}
		case 'HandwrittenCardAutomationStep': {
			const hwcStep = step as Api.HandwrittenCardAutomationStep;
			return Boolean(hwcStep.content);
		}
		default:
			return true;
	}
}
